/**
 * NoiseAuditPanel
 *
 * Surfaces the noise audit for a completed session. Three sections:
 *
 * 1. Headline — noise index meter + level + summary
 * 2. Rank Uncertainty Map — the killer visualization. Shows each item's
 *    nominated rank alongside its plausible range. Groups of items with
 *    overlapping bands are highlighted as "statistically tied" — making
 *    the false precision of the ranking visible at a glance.
 * 3. Item detail — each item categorized as Contested / Normal / Uniform /
 *    Suspiciously Uniform, expandable for score distribution.
 * 4. Anchor drift callout (if detected).
 */

import { useState } from 'react';
import {
  Activity,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Info,
  Eye,
  Waves,
} from 'lucide-react';
import type { SessionNoiseAudit, ItemNoiseProfile, SessionNoiseLevel } from '../../../types/prioritization/noiseAudit';
import { NOISE_LEVEL_META, ITEM_NOISE_META } from '../../../lib/noiseAuditService';

// ── Noise index meter ─────────────────────────────────────────────────────────

function NoiseIndexMeter({ index, level }: { index: number; level: SessionNoiseLevel }) {
  const meta = NOISE_LEVEL_META[level];
  const pct = Math.min(100, Math.round(index * 100));

  // Track has 4 zones: 0–12 (low/blue), 12–25 (healthy/green), 25–40 (elevated/amber), 40–100 (high/red)
  const zones = [
    { end: 12,  color: 'bg-[var(--slate)]'  },
    { end: 25,  color: 'bg-[var(--green)]' },
    { end: 40,  color: 'bg-[var(--mustard)]' },
    { end: 100, color: 'bg-[var(--coral)]'   },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between">
        <div>
          <span className={`text-3xl font-bold tabular-nums ${meta.color}`}>{pct}</span>
          <span className={`text-lg font-medium ml-0.5 ${meta.color}`}>%</span>
          <span className="ml-2 text-xs text-[var(--text-ter)] font-medium uppercase tracking-wide">Noise Index</span>
        </div>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${meta.bg} ${meta.border} ${meta.color}`}>
          {meta.label} noise
        </span>
      </div>

      {/* Segmented track */}
      <div className="relative h-2.5 rounded-full bg-[var(--hover-tint)] overflow-hidden flex">
        {zones.map((z, i) => {
          const prevEnd = i === 0 ? 0 : zones[i - 1].end;
          const zoneWidth = z.end - prevEnd;
          const filled = Math.max(0, Math.min(pct - prevEnd, zoneWidth));
          return (
            <div key={i} className="relative" style={{ width: `${zoneWidth}%` }}>
              <div
                className={`absolute inset-y-0 left-0 ${z.color} transition-all`}
                style={{ width: `${(filled / zoneWidth) * 100}%` }}
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-between text-[9px] text-[var(--text-ter)]">
        <span>Low</span>
        <span>Healthy</span>
        <span>Elevated</span>
        <span>High</span>
      </div>

      <p className="text-xs text-[var(--text-ter)]">{meta.description}</p>
    </div>
  );
}

// ── Rank uncertainty map ──────────────────────────────────────────────────────

function RankUncertaintyMap({ audit }: { audit: SessionNoiseAudit }) {
  const profiles = [...audit.itemProfiles].sort((a, b) => a.nominatedRank - b.nominatedRank);
  const maxRank = profiles.length;
  if (maxRank === 0) return null;

  // Build a set of item IDs that are in a tied cluster
  const tiedIds = new Set(audit.tiedClusters.flatMap(c => c.itemIds));

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold text-[var(--text-sec)] mb-0.5">Rank Uncertainty Map</p>
        <p className="text-[11px] text-[var(--text-ter)]">
          Each bar spans the plausible rank range given the scoring noise (±1.5σ).
          Items whose bands overlap cannot be meaningfully ordered.
        </p>
      </div>

      <div className="space-y-1.5">
        {profiles.slice(0, 12).map(profile => {
          const { optimisticRank, pessimisticRank } = profile.rankBand;
          const rangeWidth = pessimisticRank - optimisticRank;
          const isTied = tiedIds.has(profile.itemId);

          // Position as % of total rank space
          const leftPct  = ((optimisticRank  - 1) / maxRank) * 100;
          const widthPct = (rangeWidth / maxRank) * 100;
          const pinPct   = ((profile.nominatedRank - 1) / maxRank) * 100;

          const barColor = isTied
            ? 'bg-[var(--mustard-dim)]'
            : profile.noiseCategory === 'contested'
            ? 'bg-[var(--coral-dim)]'
            : profile.noiseCategory === 'suspicious_uniform'
            ? 'bg-[var(--slate-dim)]'
            : 'bg-[var(--slate-dim)]';

          const pinColor = isTied ? 'bg-[var(--mustard)]' : 'bg-[var(--surface)]';

          return (
            <div key={profile.itemId} className="flex items-center gap-3">
              {/* Rank number */}
              <div className="w-5 text-right text-[10px] font-semibold text-[var(--text-ter)] tabular-nums flex-shrink-0">
                #{profile.nominatedRank}
              </div>

              {/* Title */}
              <div className="w-36 flex-shrink-0">
                <p className={`text-[11px] truncate ${isTied ? 'text-[var(--mustard)] font-medium' : 'text-[var(--text-sec)]'}`}>
                  {profile.itemTitle}
                </p>
              </div>

              {/* Band track */}
              <div className="flex-1 relative h-4 bg-[var(--hover-tint)] rounded-full overflow-visible">
                {/* Range band */}
                {rangeWidth > 0 && (
                  <div
                    className={`absolute inset-y-0.5 rounded-full ${barColor}`}
                    style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 2)}%` }}
                  />
                )}
                {/* Nominated rank pin */}
                <div
                  className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${pinColor} ring-2 ring-white`}
                  style={{ left: `calc(${pinPct}% - 4px)` }}
                />
              </div>

              {/* CV badge */}
              <div className="w-14 flex-shrink-0">
                <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${ITEM_NOISE_META[profile.noiseCategory].bg} ${ITEM_NOISE_META[profile.noiseCategory].color}`}>
                  {profile.noiseCategory === 'suspicious_uniform' ? 'Uniform?' :
                   profile.noiseCategory === 'uniform' ? 'Aligned' :
                   profile.noiseCategory === 'contested' ? 'Contested' : 'Normal'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tied cluster callouts */}
      {audit.tiedClusters.length > 0 && (
        <div className="space-y-1.5 pt-1">
          {audit.tiedClusters.map((cluster, i) => (
            <div key={i} className="flex items-start gap-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2">
              <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[var(--mustard)]">{cluster.clusterLabel}</p>
                <p className="text-[11px] text-[var(--mustard)] mt-0.5">{cluster.explanation}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 pt-1 border-t border-[var(--rule)]">
        {[
          { color: 'bg-[var(--surface)]', label: 'Nominated rank' },
          { color: 'bg-[var(--slate-dim)] border border-[var(--slate)]', label: 'Plausible range' },
          { color: 'bg-[var(--mustard-dim)]', label: 'Tied with neighbor' },
          { color: 'bg-[var(--coral-dim)]', label: 'Contested' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-full ${l.color}`} />
            <span className="text-[10px] text-[var(--text-ter)]">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Item detail row ───────────────────────────────────────────────────────────

function ItemNoiseRow({ profile }: { profile: ItemNoiseProfile }) {
  const [open, setOpen] = useState(false);
  const meta = ITEM_NOISE_META[profile.noiseCategory];
  const showDetail =
    profile.noiseCategory === 'contested' || profile.noiseCategory === 'suspicious_uniform';

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--surface)] transition-colors"
      >
        <span className="text-xs font-semibold text-[var(--text-ter)] tabular-nums w-5">#{profile.nominatedRank}</span>
        <span className="flex-1 text-sm text-[var(--text-pri)] truncate">{profile.itemTitle}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
          {meta.label}
        </span>
        <span className="text-[10px] text-[var(--text-ter)] tabular-nums ml-1">
          CV {(profile.coefficientOfVariation * 100).toFixed(0)}%
        </span>
        {showDetail && (
          open ? <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)]" /> : <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        )}
      </button>

      {open && showDetail && (
        <div className="px-4 pb-3 pt-1 border-t border-[var(--rule)] space-y-2">
          <p className="text-[11px] text-[var(--text-ter)]">{meta.description}</p>

          {/* Per-participant scores */}
          <div className="space-y-1">
            <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Individual scores</p>
            <div className="flex flex-wrap gap-2">
              {profile.participantScores.map(ps => (
                <div key={ps.participantId} className="flex items-center gap-1 bg-[var(--surface)] border border-[var(--rule)] px-2 py-1">
                  <span className="text-[10px] text-[var(--text-ter)] truncate max-w-[80px]">{ps.participantName}</span>
                  <span className="text-[10px] font-semibold text-[var(--text-pri)] tabular-nums">{ps.score.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Score distribution bar */}
          {profile.scoreRange.min !== profile.scoreRange.max && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[10px] text-[var(--text-ter)]">
                <span>{profile.scoreRange.min.toFixed(1)}</span>
                <span className="font-medium text-[var(--text-sec)]">μ={profile.mean.toFixed(1)} σ={profile.stdDev.toFixed(1)}</span>
                <span>{profile.scoreRange.max.toFixed(1)}</span>
              </div>
              <div className="relative h-1.5 bg-[var(--hover-tint)] rounded-full">
                {/* Mean marker */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[var(--text-ter)] ring-2 ring-white"
                  style={{
                    left: `calc(${((profile.mean - profile.scoreRange.min) / (profile.scoreRange.max - profile.scoreRange.min + 0.01)) * 100}% - 4px)`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Rank band */}
          <p className="text-[11px] text-[var(--text-ter)]">
            Plausible rank: <span className="font-medium text-[var(--text-sec)]">#{profile.rankBand.optimisticRank}–#{profile.rankBand.pessimisticRank}</span>
            {' '}(nominated #{profile.nominatedRank})
            {!profile.rankBand.isStatisticallyDistinct && (
              <span className="ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
                tied with neighbor
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Anchor drift callout ──────────────────────────────────────────────────────

function AnchorDriftCallout({ audit }: { audit: SessionNoiseAudit }) {
  if (!audit.anchorDrift) return null;
  const { anchorDrift } = audit;

  if (!anchorDrift.detected) return null;

  return (
    <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 text-[var(--coral)]" />
        <p className="text-sm font-semibold text-[var(--coral)]">Anchor Drift Detected</p>
        <span className="ml-auto text-xs font-semibold text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] rounded-full px-2 py-0.5">
          r = {anchorDrift.meanFollowerCorrelation}
        </span>
      </div>
      <p className="text-xs text-[var(--coral)] leading-relaxed">{anchorDrift.interpretation}</p>
      <div className="pt-1">
        <p className="text-[10px] font-semibold text-[var(--coral)] uppercase tracking-wide mb-1.5">Correlation with first voter ({anchorDrift.firstVoterName})</p>
        <div className="space-y-1">
          {anchorDrift.followerCorrelations.slice(0, 6).map(f => (
            <div key={f.participantId} className="flex items-center gap-2">
              <span className="text-[10px] text-[var(--coral)] w-4 text-right">#{f.submissionOrder}</span>
              <span className="text-[10px] text-[var(--coral)] flex-1 truncate">{f.participantName}</span>
              <div className="w-24 h-1.5 bg-[var(--coral-dim)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--coral)] rounded-full"
                  style={{ width: `${Math.max(0, f.correlationWithFirstVoter * 100)}%` }}
                />
              </div>
              <span className="text-[10px] font-medium text-[var(--coral)] tabular-nums">
                {(f.correlationWithFirstVoter * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-[var(--bg-raised)]/60 border border-[var(--coral)] px-3 py-2">
        <p className="text-[11px] text-[var(--coral)] font-medium">Countermeasure</p>
        <p className="text-[11px] text-[var(--coral)] mt-0.5">
          Switch to hidden voting mode so participants cannot see others' scores while submitting.
          Re-run the session and compare the distributions.
        </p>
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

interface NoiseAuditPanelProps {
  audit: SessionNoiseAudit;
}

type PanelSection = 'map' | 'items' | 'anchor';

export function NoiseAuditPanel({ audit }: NoiseAuditPanelProps) {
  const [activeSection, setActiveSection] = useState<PanelSection>('map');
  const [collapsed, setCollapsed] = useState(false);

  const hasAnchorDrift = audit.anchorDrift?.detected === true;
  const hasWarnings = audit.contestedItems.length > 0 ||
    audit.suspiciouslyUniformItems.length > 0 ||
    hasAnchorDrift;

  const tabClass = (s: PanelSection) =>
    `px-3 py-1.5 text-xs font-medium  transition-colors ${
      activeSection === s
        ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
        : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
    }`;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-[var(--green-dim)] flex items-center justify-center flex-shrink-0">
            <Waves className="h-4 w-4 text-[var(--green)]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-[var(--text-pri)]">Noise Audit</p>
              {hasWarnings && (
                <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
                  {[
                    audit.contestedItems.length > 0 && `${audit.contestedItems.length} contested`,
                    audit.suspiciouslyUniformItems.length > 0 && `${audit.suspiciouslyUniformItems.length} uniform?`,
                    hasAnchorDrift && 'anchor drift',
                  ].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--text-ter)]">
              {audit.participantCount} participants · {audit.itemCount} items ·{' '}
              {new Date(audit.computedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
        >
          {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </button>
      </div>

      {!collapsed && (
        <div className="p-5 space-y-5">
          {/* Noise meter */}
          <NoiseIndexMeter index={audit.noiseIndex} level={audit.noiseLevel} />

          {/* Key findings */}
          {audit.keyFindings.length > 0 && (
            <div className="space-y-1.5">
              {audit.keyFindings.map((finding, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Activity className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[var(--text-sec)] leading-relaxed">{finding}</p>
                </div>
              ))}
            </div>
          )}

          {/* Section tabs */}
          <div className="bg-[var(--hover-tint)] p-1 flex gap-0.5">
            <button className={tabClass('map')} onClick={() => setActiveSection('map')}>
              Rank Map
            </button>
            <button className={tabClass('items')} onClick={() => setActiveSection('items')}>
              Items ({audit.itemProfiles.length})
            </button>
            {hasAnchorDrift && (
              <button className={tabClass('anchor')} onClick={() => setActiveSection('anchor')}>
                <span className="flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-[var(--coral)]" />
                  Anchor Drift
                </span>
              </button>
            )}
          </div>

          {/* Section content */}
          {activeSection === 'map' && <RankUncertaintyMap audit={audit} />}

          {activeSection === 'items' && (
            <div className="space-y-1.5">
              {audit.itemProfiles.map(profile => (
                <ItemNoiseRow key={profile.itemId} profile={profile} />
              ))}
            </div>
          )}

          {activeSection === 'anchor' && <AnchorDriftCallout audit={audit} />}

          {/* Precision statement */}
          {audit.precisionStatement && (
            <div className="flex items-start gap-2 pt-1 border-t border-[var(--rule)]">
              <Info className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[var(--text-ter)] leading-relaxed italic">
                {audit.precisionStatement}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
